import {
  Card,
  Text,
  SimpleGrid,
  Group,
  Button,
  NumberInput,
  rem
} from '@mantine/core';
import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { getFetcher, postFetcher } from '@/api/fetchers';
import type { Service } from '@/schemas/API/GET/service/response';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconX, IconThumbUp } from '@tabler/icons-react';

interface CustomPriceProps {
  clientId: string | undefined;
  service: Service | undefined;
}

export const CustomPriceCard: React.FC<CustomPriceProps> = ({
  clientId,
  service,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: currentPriceData,
    error,
    mutate,
  } = useSWR<number>(
    clientId && service
      ? `/clients/${clientId}/service-cost/${service.id}`
      : null,
    getFetcher
  );

  useEffect(() => {
    if (currentPriceData !== undefined) {
      form.setFieldValue('newPrice', currentPriceData);
    }
  }, [currentPriceData]);

  const form = useForm({
    initialValues: {
      newPrice: currentPriceData ?? 0, 
    },

    
    validate: {
      newPrice: (value) => {
        return value < 0 ? 'Please enter a valid number greater than or equal to 0' : null;
      },
    },
  });

  const handleSave = async () => {
    const numericDiscount = form.values.newPrice;

    if (isNaN(numericDiscount) || numericDiscount < 0) {
      return;
    }

    setIsSaving(true);

    try {
      await postFetcher(`/clients/${clientId}/service-cost/${service?.id}?price=${numericDiscount}`);
      mutate();
    } catch (err) {
      notifications.show({
              title: 'Ve formuláři se vyskytla chyba',
              message: 'Zkontrolujte, že položky nejsou prázdné',
              color: 'red',
              icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
            });
      console.error('Error saving discount:', err);
    } finally {
      setTimeout(() => {
        notifications.show({
          title: 'Cena úspěšně upravena',
          message: `Nová cena je ${numericDiscount} Kč`,
          color: 'blue',
          icon: <IconThumbUp style={{ width: rem(20), height: rem(20) }} />,
        });
      }, 200);
      
      setIsSaving(false);
    }
  };

  if (error) {
    return <Text c="red">Chyba při načítání slevy.</Text>;
  }

  return (
    <Card shadow="sm" radius="md" withBorder>
      <SimpleGrid verticalSpacing="sm">
        <Group justify="space-between">
          <Text fw={700} size="lg">
            Individuální cena pro vybranou službu
          </Text>

          <Text fw={500} size="lg">
            Současná cena: {currentPriceData} Kč
          </Text>
        </Group>

        <form onSubmit={form.onSubmit(handleSave)}>
          <NumberInput
            label="Nová cena (Kč)"
            placeholder="Zadejte novou cenu pro klienta"
            value={form.values.newPrice}
            onChange={(value) => {
              const numericValue = typeof value === 'number' ? value : parseFloat(value);
              form.setFieldValue('newPrice', isNaN(numericValue) ? currentPriceData ?? 0 : numericValue);
            }}
            error={form.errors.newPrice}
            disabled={isSaving}
            data-testid="note-input"
            min={0} 
            hideControls
            required
          />

          <Group mt="md">
            <Button
              type="submit"
              loading={isSaving}
              disabled={isSaving || !form.isValid() || form.values.newPrice < 0}
            >
              Přidat
            </Button>
          </Group>
        </form>
      </SimpleGrid>
    </Card>
  );
};
